import React from 'react';
import {
  Box, Button, Input, Stack, Typography,
} from '@mui/material';
import UploadIcon from '../../../images/upload-image.webp';
import LibraryIcon from '../../../images/library.webp';
import SelectColorIcon from '../../../images/select-color.webp';
import appStore, { ThumbnailSource } from '../../../store/AppStore';

interface Props {
  handleFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void,
  handleLibraryView: () => void,
  handleColorView: () => void,
  handleSaveSet: () => void,
  disableSave: boolean,
  characterSetTitle: string,
  setCharacterTitle: (value: string) => void,
}

/**
 * Represents a React component for the general view of the create Character Set Modal.
 *
 * @component
 * @example
 * // Example usage of CharacterGeneralView component
 * <CharacterGeneralView
 *   handleFileChange={handleFileChangeFunction}
 *   handleLibraryView={handleLibraryViewFunction}
 *   handleColorView={handleColorViewFunction}
 * />
 * @date 11/22/2023 - 11:56:14 AM
 *
 * @param {Props} param0
 * @param {(event: React.ChangeEvent<HTMLInputElement>) => void} param0.handleFileChange
 * @param {() => void} param0.handleLibraryView
 * @param {() => void} param0.handleColorView
 * @returns {*}
 */
function CharacterGeneralView({
  handleFileChange,
  handleLibraryView,
  handleColorView,
  handleSaveSet,
  disableSave,
  characterSetTitle,
  setCharacterTitle,
}: Props) {
  /**
   * The selected thumbnail source from the application store.
   *
   * @type {ThumbnailSource}
   */
  const selectedThumbnailSource: ThumbnailSource = appStore(
    (state) => state.previewThumbnailSource,
  );

  return (
    <>
      {/* Character Set Name */}
      <Box>
        <Typography
          sx={{
            color: '#250503',
            fontFamily: 'DM Sans',
            fontSize: '13px',
            fontStyle: 'normal',
            fontWeight: '700',
            lineHeight: 'normal',
          }}
        >
          Character Set Name
        </Typography>
        <Input
          placeholder="Lorem ipsum dolor sit"
          value={characterSetTitle}
          onChange={
            (event: React.ChangeEvent<HTMLInputElement>) => setCharacterTitle(event.target.value)
          }
          autoFocus
          fullWidth
          sx={{
            borderRadius: '6px',
            border: '2px solid #E0E0E0',
            p: '10px 12px',
            outline: 'none',
            filter: 'none',
            mt: '10px',
            boxShadow: 'none',
            '&::before, &::after': {
              outline: 'none!important',
              filter: 'none!important',
              boxShadow: 'none!important',
              border: 'none!important',
            },
            color: '#A5ACB8',
            fontFamily: 'DM Sans',
            fontSize: '13px',
            fontStyle: 'normal',
            fontWeight: '700',
            lineHeight: 'normal',
          }}
        />
      </Box>
      {/* Select a thumbnail */}
      <Box mt="45px">
        <Typography
          sx={{
            color: '#250503',
            fontFamily: 'DM Sans',
            fontSize: '13px',
            fontStyle: 'normal',
            fontWeight: '700',
            lineHeight: 'normal',
          }}
        >
          Select a Thumbnail
        </Typography>
        <Stack spacing="50px" mt="10px" direction="row">
          {/* upload image */}
          <Stack spacing="6px" alignItems="center" direction="column">
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor="fileInput">
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  borderRadius: '5px',
                  backgroundColor:
                    selectedThumbnailSource === 'upload'
                      ? '#41521F'
                      : '#7A3900',
                  minHeight: '60px',
                  minWidth: '60px',
                  cursor: 'pointer',
                }}
              >
                <img
                  src={UploadIcon}
                  alt="upload"
                  style={{
                    height: 'auto',
                    width: '30px',
                  }}
                />
              </Box>
            </label>
            <input
              type="file"
              accept=".jpg,.png,.webp"
              style={{ display: 'none' }}
              onChange={handleFileChange}
              id="fileInput"
            />
            <Typography
              sx={{
                color: '#250503',
                fontFamily: 'DM Sans',
                fontSize: '13px',
                fontStyle: 'normal',
                fontWeight: '500',
              }}
            >
              Upload an Image
            </Typography>
          </Stack>
          {/* library */}
          <Stack spacing="6px" alignItems="center" direction="column">
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                borderRadius: '5px',
                backgroundColor:
                  selectedThumbnailSource === 'library' ? '#41521F' : '#7A3900',
                minHeight: '60px',
                minWidth: '60px',
                cursor: 'pointer',
              }}
              onClick={handleLibraryView}
            >
              <img
                src={LibraryIcon}
                alt="upload"
                style={{
                  height: 'auto',
                  width: '30px',
                }}
              />
            </Box>
            <Typography
              sx={{
                color: '#250503',
                fontFamily: 'DM Sans',
                fontSize: '13px',
                fontStyle: 'normal',
                fontWeight: '500',
              }}
            >
              Choose from Library
            </Typography>
          </Stack>
          {/* color */}
          <Stack spacing="6px" alignItems="center" direction="column">
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                borderRadius: '5px',
                backgroundColor:
                  selectedThumbnailSource === 'color' ? '#41521F' : '#7A3900',
                minHeight: '60px',
                minWidth: '60px',
                cursor: 'pointer',
              }}
              onClick={handleColorView}
            >
              <img
                src={SelectColorIcon}
                alt="upload"
                style={{
                  height: 'auto',
                  width: '30px',
                }}
              />
            </Box>
            <Typography
              sx={{
                color: '#250503',
                fontFamily: 'DM Sans',
                fontSize: '13px',
                fontStyle: 'normal',
                fontWeight: '500',
              }}
            >
              Select Color
            </Typography>
          </Stack>
        </Stack>
      </Box>
      {/* Save Set Button */}
      <Box mt="50px" width="100%" display="flex" justifyContent="flex-end">
        <Button
          variant="contained"
          sx={{
            borderRadius: '4px',
            background: '#41521F',
            boxShadow:
              '0px 3px 1px -2px rgba(0, 0, 0, 0.20), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 1px 5px 0px rgba(0, 0, 0, 0.12)',
            color: '#FFF',
            fontFamily: 'Passero One',
            fontSize: '16px',
            fontStyle: 'normal',
            fontWeight: '400',
            lineHeight: '22px' /* 137.5% */,
            letterSpacing: '0.46px',
            textTransform: 'uppercase',
            '&:hover': {
              background: '#41521F',
            },
          }}
          onClick={handleSaveSet}
          disabled={disableSave}
        >
          Save Set
        </Button>
      </Box>
    </>
  );
}

export default CharacterGeneralView;
